/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useMemo } from "react";
import { X } from "lucide-react";
import { useSearchParams } from "next/navigation";

import ContentLayout from "../layouts/ContentLayout";
import AppText from "../other/AppText";
import { IDict, ITEMS_PER_PAGE } from "../other/constants";
import { ControlledCustomSelect } from "./CustomSelect";
import ShowroomCard from "./ShowroomCard";
import LoadingDots from "../other/LoadingDots";
import Pagination from "../other/Pagination";

const SORT_CARDS_MAP: string[] = ["Price (Low to High)", "Price (High to Low)", "Newest to Oldest", "Oldest to Newest"];

const SORT_TAGS_MAP: { [key: string]: string } = {
  "Price (Low to High)": "Order: Price Lowest",
  "Price (High to Low)": "Order: Price Highest",
  "Newest to Oldest": "Order: Newest",
  "Oldest to Newest": "Order: Oldest",
};

const SearchResultsCards = ({
  dict,
  units,
  handleTagClick,
  sort,
  setSort,
  loading,
  total,
  setCurrentPage,
  currentPage,
}: {
  dict: IDict;
  units: any;
  handleTagClick: (tag: string) => void;
  sort: string;
  setSort: (sort: string) => void;
  loading: boolean;
  total: number;
  setCurrentPage: (page: number) => void;
  currentPage: number;
}) => {
  const searchParams = useSearchParams();
  const lastPage = Math.ceil(total / ITEMS_PER_PAGE);
  const tags = useMemo(() => {
    const tmpParams: any = {
      coordinates: searchParams.get("coordinates") || undefined,
      name: searchParams.get("name") || undefined,
      distance: searchParams.get("distance") || undefined,
      priceRange: searchParams.get("price-range") || undefined,
      unitType: searchParams.get("unit-type") || undefined,
      unitId: searchParams.get("unit-id") || undefined,
    };

    const res = Object.keys(tmpParams).map((item) => {
      if (tmpParams[item] && item !== "name" && item !== "coordinates") {
        return {
          type: item,
          value: tmpParams[item],
        };
      }

      return null;
    });

    return res.filter((item) => item);
  }, [searchParams]);

  const handleSortChange = (value: string) => {
    setSort(value);
  };

  return (
    <ContentLayout bgColorClass="bg-white" id="usedProductResults">
      <div className="lg:flex-row lg:items-center flex flex-col justify-between gap-4 mb-4">
        <div className="flex flex-col">
          <AppText type="TITLE_MEDIUM" as="h1">
            search results
          </AppText>
          <AppText type="BODY_SMALL">
            {units.total || 0} {units.total === "1" ? "result" : "results"}
          </AppText>
        </div>

        <div className="min-w-[200px]">
          <ControlledCustomSelect
            title="sort results by"
            value={sort}
            name="sort"
            dict={dict}
            data={SORT_CARDS_MAP}
            onChange={handleSortChange}
            placeholder="sortBy"
            disabled={loading}
          />
        </div>
      </div>

      {/**   tags  */}
      <div className="flex flex-wrap w-full gap-4 mb-8">
        {(tags || []).map((tag: any, index: number) => (
          <div className="bg-black-10 flex items-center gap-2 py-1 pl-4 pr-1 rounded" key={index}>
            {tag.type === "priceRange" || tag.type === "distance"
              ? dict[tag.value]
              : tag.type === "unitId"
              ? `Unit #: ${tag.value}`
              : tag.value}
            <button
              aria-label="clear tag"
              id="button-search-results-cards-tag"
              onClick={() => handleTagClick(tag.type)}
              disabled={loading}
              className="hover:bg-black-20 p-1 rounded"
            >
              <X size={16} aria-label="x icon" />
            </button>
          </div>
        ))}

        {sort && (
          <div className="bg-black-10 flex items-center gap-2 py-1 pl-4 pr-1 rounded">
            {SORT_TAGS_MAP[sort]}
            <button
              id="button-search-results-cards-tag-sort"
              onClick={() => handleTagClick("sort")}
              disabled={loading}
              className="hover:bg-black-20 p-1 rounded"
            >
              <X size={16} aria-label="x icon" />
            </button>
          </div>
        )}
      </div>

      {/** cards */}
      {loading ? (
        <LoadingDots />
      ) : (
        <div className="lg:grid-cols-12 sm:gap-6 xl:gap-10 grid grid-cols-4 gap-x-2 gap-y-[56px]">
          {units.cards.map((card: any) => (
            <ShowroomCard key={card.id} card={card} />
          ))}
        </div>
      )}

      {total > 12 && (
        <div className="flex justify-center px-6 py-[48px] mt-8">
          <Pagination currentPage={currentPage} lastPage={lastPage} setCurrentPage={setCurrentPage} />
        </div>
      )}
    </ContentLayout>
  );
};

export default SearchResultsCards;
